import type { DateTime } from "luxon";
import type { Department } from "../../contexts/AuthProvider";
import { resolveCallInTime } from "../../utils/callIns";
import type { CalledOffShift, ChangesSource, HospitalData, NurseInShift } from "../../utils/models";
import { NurseTrainingStatus } from "../../utils/models";
import { getJobDisplayName } from "../../utils/roles";
import { isAgencyNurse } from "../../utils/warningDialogs/utils";
import { resolveNurseName } from "../dailyPrintout/render/callOutsSection";
import type { ScheduleRow } from "../dailyPrintout/types";
import { resolveRoleShortCode } from "../dailyPrintout/util/roleHelpers";
import { getRowScheduledHours } from "../dailyPrintout/util/scheduledHours";
import { formatShiftTimeRange } from "../dailyPrintout/util/shiftTimeFormat";
import type { FindDepartmentLocation } from "./aiPrintoutCensusGroups";
import type { AiPrintoutCallOut, AiPrintoutPto, AiPrintoutShift } from "./contract";

/** Matches the Detailed printout's call-out "Called At" cell. */
const CALLED_AT_FORMAT = "ccc, MMM d, h:mm a";

/** Everything a mapper needs to place a time on the department's schedule day. */
export type AiPrintoutTimeContext = {
	timezone: string;
	/** The instant the schedule day starts (department day start, department timezone). */
	dayStartAt: DateTime;
	locale: string;
};

export const toZonedIso = (time: DateTime, context: AiPrintoutTimeContext): string => {
	const iso = time.setZone(context.timezone).toISO();
	if (iso === null) throw new Error(`Invalid shift time: ${time.invalidExplanation}`);
	return iso;
};

/** Whole minutes from the schedule day's start, so a 06:00 shift under a 07:00 day start is -60. */
export const minutesFromDayStart = (time: DateTime, context: AiPrintoutTimeContext): number => Math.round(time.diff(context.dayStartAt, "minutes").minutes);

/** The time fields every row kind shares. */
export const timeFields = (start: DateTime, end: DateTime, context: AiPrintoutTimeContext) => ({
	start: toZonedIso(start, context),
	end: toZonedIso(end, context),
	timeLabel: formatShiftTimeRange(start, end, context.timezone),
	startMinutesFromDayStart: minutesFromDayStart(start, context),
});

/** First letter of each word of an already-formatted name, so initials follow the department's name order. */
export const initialsOf = (name: string): string =>
	name
		.split(/\s+/)
		.filter((word) => word.length > 0)
		.map((word) => word.charAt(0).toUpperCase())
		.join("");

export const roleFields = (role: string) => ({ role, roleLabel: role, roleShort: resolveRoleShortCode(role, undefined) });

/** A text field the source leaves blank or out when the row has none, as the contract's null. */
export const textOrNull = (value: string | undefined): string | null => (value === undefined || value === "" ? null : value);

/** The department's location and role orders as value → rank, built once per printout (see `AiPrintoutRanks`). */
export type AiPrintoutOrders = {
	locationRanks: ReadonlyMap<string, number>;
	roleRanks: ReadonlyMap<string, number>;
};

/** Every location and role on the page is put in the orders up front, so a missing rank is a bug. */
export const rankIn = (ranks: ReadonlyMap<string, number>, value: string): number => {
	const rank = ranks.get(value);
	if (rank === undefined) throw new Error(`"${value}" has no rank in the printout's orders`);
	return rank;
};

/** The job's place in the department's job order (`Job.sortOrder`). */
export const jobRankOf = (jobId: string | null, department: Department): number | null => {
	if (jobId === null) return null;
	const job = department.jobs?.find((candidate) => candidate.id === jobId);
	// A job taken off the department since the shift was made has no place in its order: a real state, not missing data.
	return job === undefined ? null : job.sortOrder;
};

const pendingOf = (shift: NurseInShift): AiPrintoutShift["pending"] => {
	if (shift.isPendingToDelete) return "delete";
	if (shift.isPending) return "change";
	return "none";
};

const jobFields = (shift: NurseInShift, department: Department, jobManagementEnabled: boolean) => {
	const jobId = jobManagementEnabled && shift.jobId ? shift.jobId : null;
	return { jobId, jobName: jobId === null ? null : getJobDisplayName(jobId, department) };
};

/** The shift fields that come from the rest of the day (`createShiftFacts`), not from the staff row itself. */
export type AiPrintoutShiftFactKey =
	| "notOnboarded"
	| "scheduled"
	| "actual"
	| "timeGroupKeys"
	| "countsTowardHppd"
	| "countsTowardDhppd"
	| "countsTowardNhppd"
	| "hppdHours"
	| "countsTowardPpd";

interface MapStaffRowArgs {
	row: ScheduleRow;
	department: Department;
	jobManagementEnabled: boolean;
	context: AiPrintoutTimeContext;
	orders: AiPrintoutOrders;
	/** The Day view's location resolver, which `metrics.byLocation` groups shifts by. */
	findDepartmentLocation: FindDepartmentLocation;
}

/**
 * A staffed source row (Detailed's own row, so hours match it) as a contract shift's own fields. The floor is
 * the Day view's location row's, not the row's: with Job Management on, a location name with rows for several
 * jobs resolves by the shift's job there, so a shift's floor is the floor `byLocation` counts it under.
 */
export const mapStaffRow = ({
	row,
	department,
	jobManagementEnabled,
	context,
	orders,
	findDepartmentLocation,
}: MapStaffRowArgs): Omit<AiPrintoutShift, AiPrintoutShiftFactKey> => {
	const shift = row.nurse;
	const hours = getRowScheduledHours({ row, department, jobManagementEnabled });
	if (!shift || hours === undefined) throw new Error("mapStaffRow needs a staffed row");
	const location = textOrNull(shift.location);
	const jobs = jobFields(shift, department, jobManagementEnabled);
	return {
		id: shift.id,
		nurseId: shift.nurseId,
		name: shift.name,
		initials: initialsOf(shift.name),
		...roleFields(shift.role),
		...jobs,
		location,
		floor: textOrNull(findDepartmentLocation(shift, department.locations)?.floor),
		locationRank: location === null ? null : rankIn(orders.locationRanks, location),
		roleRank: rankIn(orders.roleRanks, shift.role),
		jobRank: jobRankOf(jobs.jobId, department),
		...timeFields(shift.startTime, shift.endTime, context),
		hours,
		charge: shift.charge,
		employmentType: shift.type,
		isAgency: isAgencyNurse(shift.type),
		trainingStatus: shift.trainingStatus === NurseTrainingStatus.Regular ? null : shift.trainingStatus,
		notes: shift.notes,
		breaks: [...shift.breaks]
			.sort((first, second) => first.startTime.toMillis() - second.startTime.toMillis())
			.map((recordedBreak) => ({
				start: toZonedIso(recordedBreak.startTime, context),
				end: toZonedIso(recordedBreak.endTime, context),
				label: formatShiftTimeRange(recordedBreak.startTime, recordedBreak.endTime, context.timezone),
			})),
		pending: pendingOf(shift),
	};
};

interface MapPtoShiftArgs {
	shift: NurseInShift;
	/** The Detailed printout's time-off reason (`timeOffReasonOf`), or null when the viewer leaves reasons off. */
	reason: string | null;
	timeGroupKeys: string[] | null;
	context: AiPrintoutTimeContext;
}

export const mapPtoShift = ({ shift, reason, timeGroupKeys, context }: MapPtoShiftArgs): AiPrintoutPto => ({
	nurseId: shift.nurseId,
	name: shift.name,
	initials: initialsOf(shift.name),
	...roleFields(shift.role),
	location: textOrNull(shift.location),
	...timeFields(shift.startTime, shift.endTime, context),
	reason,
	timeGroupKeys,
});

interface MapCallOutArgs {
	callOut: CalledOffShift;
	hospitalData: HospitalData;
	lastNameFirst: boolean;
	callOffSourceLabel: (source: ChangesSource | null | undefined) => string;
	timeGroupKeys: string[] | null;
	context: AiPrintoutTimeContext;
}

export const mapCallOut = ({ callOut, hospitalData, lastNameFirst, callOffSourceLabel, timeGroupKeys, context }: MapCallOutArgs): AiPrintoutCallOut => {
	const name = resolveNurseName(callOut, hospitalData, lastNameFirst);
	const calledAt = resolveCallInTime(callOut).setZone(context.timezone);
	return {
		nurseId: callOut.nurseId,
		name,
		initials: initialsOf(name),
		...timeFields(callOut.startTime, callOut.endTime, context),
		calledAt: toZonedIso(calledAt, context),
		calledAtLabel: calledAt.setLocale(context.locale).toFormat(CALLED_AT_FORMAT),
		// DEFAULT: blank when the caller gave no reason or the manager left no note — real data states.
		reason: callOut.message ?? "",
		sourceLabel: callOffSourceLabel(callOut.deletedSource),
		notes: callOut.removalMessage ?? "",
		timeGroupKeys,
	};
};
